import {
  EyeOutlined,
  LeftOutlined,
  RightOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { Alert, Button, Table, TableColumnsType, Tag, Tooltip } from 'antd'
import dayjs from 'dayjs'

import { ONLY_DATE_TIME_FORMAT } from '../../../../../constants/global'
import PageUrls from '../../../../../constants/page-urls'
import { numberFormat } from '../../../../../utils'
import { ISaleOrderListItem } from '../types'

interface Props {
  items: ISaleOrderListItem[]
  isLoading: boolean
  error: boolean
  startIndex: number
  hasNext: boolean
  hasPrev: boolean
  goNext: () => void
  goPrev: () => void
  scrollY?: number | string
}

// ตารางรายการคำสั่งซื้อ + ปุ่มเปลี่ยนหน้า — ใช้ร่วมทั้ง OrdersModal และแท็บรายการคำสั่งซื้อ
// (state/การดึงข้อมูลอยู่ที่ useSaleOrders ของฝั่งผู้เรียก)
const SaleOrdersTable = ({
  items,
  isLoading,
  error,
  startIndex,
  hasNext,
  hasPrev,
  goNext,
  goPrev,
  scrollY = 480,
}: Props) => {
  const openDetail = (orderId: string) => {
    window.open(
      `${PageUrls.Platforms.TiktokSaleOrderDetail}/${orderId}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const columns: TableColumnsType<ISaleOrderListItem> = [
    {
      title: 'ลำดับ',
      key: 'index',
      align: 'right',
      width: 64,
      render: (_: unknown, __: ISaleOrderListItem, index: number) =>
        startIndex + index + 1,
    },
    {
      title: 'เลขคำสั่งซื้อ',
      dataIndex: 'order_id',
      key: 'order_id',
    },
    {
      title: 'วันที่สั่ง',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (value: string) =>
        value ? dayjs(value).format(ONLY_DATE_TIME_FORMAT) : '-',
    },
    {
      title: 'ประเภท',
      dataIndex: 'is_cod',
      key: 'is_cod',
      render: (isCod: boolean) =>
        isCod ? <Tag color="orange">COD</Tag> : <Tag color="blue">โอน</Tag>,
    },
    {
      title: 'จำนวนชิ้น',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      width: 100,
      render: (value: number) => numberFormat(value, 0),
    },
    {
      title: 'ยอดรวม (฿)',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 100,
      align: 'right',
      render: (value: number) => numberFormat(value),
    },
    {
      title: 'ต้นทุน (฿)',
      dataIndex: 'cost_total',
      key: 'cost_total',
      width: 110,
      align: 'right',
      render: (value: number, record) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
          }}
        >
          {numberFormat(value)}
          {record.cost_missing_count > 0 && (
            <Tooltip
              title={`ต้นทุนไม่ครบ ${record.cost_missing_count} รายการ (บาง SKU ยังไม่ผูกกับสินค้าในระบบ)`}
            >
              <WarningOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'กำไรขั้นต้น (฿)',
      key: 'grossProfit',
      width: 110,
      align: 'right',
      render: (_, record) =>
        numberFormat((record.total_amount ?? 0) - (record.cost_total ?? 0)),
    },
    {
      title: 'จ่ายแล้ว',
      dataIndex: 'settled',
      key: 'settled',
      align: 'center',
      render: (settled: boolean) =>
        settled ? (
          <Tag color="green">จ่ายแล้ว</Tag>
        ) : (
          <Tag color="default">รอจ่าย</Tag>
        ),
    },
    {
      title: 'ได้รับจริง (฿)',
      dataIndex: 'net_amount',
      key: 'net_amount',
      align: 'right',
      width: 100,
      render: (value: number) => numberFormat(value),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <Tooltip title="ดูรายละเอียด">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => openDetail(record.order_id)}
          />
        </Tooltip>
      ),
    },
  ]

  return (
    <>
      {error && (
        <Alert
          type="error"
          message="ไม่สามารถโหลดรายการคำสั่งซื้อได้ กรุณาลองใหม่อีกครั้ง"
          style={{ marginBottom: 16 }}
        />
      )}
      <Table
        rowKey="order_id"
        columns={columns}
        dataSource={items}
        loading={isLoading}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content', y: scrollY }}
      />
      {(hasPrev || hasNext) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 16,
          }}
        >
          <Button
            icon={<LeftOutlined />}
            onClick={goPrev}
            disabled={!hasPrev || isLoading}
          >
            ก่อนหน้า
          </Button>
          <Button onClick={goNext} disabled={!hasNext || isLoading}>
            ถัดไป
            <RightOutlined />
          </Button>
        </div>
      )}
    </>
  )
}

export default SaleOrdersTable
